"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WatchlistDisplayService = void 0;
class WatchlistDisplayService {
    /**
     * Get movie count for a specific watchlist type
     */
    static getMovieCount(watchlistType, movieDataCache, userWatchlists = [], user) {
        var _a;
        if (!user)
            return 0;
        // Handle default watchlist types
        if (['want-to-watch', 'watched'].includes(watchlistType)) {
            let count = 0;
            movieDataCache.forEach((movieData) => {
                if (movieData[watchlistType] === true) {
                    count++;
                }
            });
            return count;
        }
        // For custom watchlists, look in userWatchlists
        const watchlist = userWatchlists.find(w => w.id === watchlistType);
        return ((_a = watchlist === null || watchlist === void 0 ? void 0 : watchlist.movieDetails) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    /**
     * Get comprehensive count information for all watchlist types
     */
    static getWatchlistCountInfo(movieDataCache, userWatchlists = []) {
        const counts = {
            totalMovies: 0,
            wantToWatch: 0,
            watched: 0,
            reviewed: 0
        };
        movieDataCache.forEach((movieData) => {
            var _a;
            if (movieData.isInWatchlist)
                counts.totalMovies++;
            if (movieData.isInWatchlist && !movieData.isWatched)
                counts.wantToWatch++;
            if (movieData.isWatched)
                counts.watched++;
            if (movieData.isWatched && ((_a = movieData.review) === null || _a === void 0 ? void 0 : _a.trim()))
                counts.reviewed++;
        });
        return counts;
    }
    /**
     * Get creator information for a watchlist
     */
    static getCreatorInfo(watchlist, currentUser) {
        if (watchlist === null || watchlist === void 0 ? void 0 : watchlist.createdByName) {
            return {
                name: watchlist.createdByName,
                avatar: watchlist.createdByAvatar,
                uid: watchlist.createdBy
            };
        }
        if ((watchlist === null || watchlist === void 0 ? void 0 : watchlist.createdBy) === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid) && currentUser) {
            return {
                name: currentUser.displayName || 'You',
                avatar: currentUser.photoURL || undefined,
                uid: currentUser.uid
            };
        }
        return {
            name: 'Anonymous User',
            avatar: undefined,
            uid: watchlist.createdBy
        };
    }
    /**
     * Format a watchlist for display purposes
     */
    static formatWatchlistForDisplay(watchlist, currentUser, movieDataCache, isFollowed) {
        var _a;
        const creator = this.getCreatorInfo(watchlist, currentUser);
        const isOwned = watchlist.createdBy === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid);
        let movieCount = 0;
        if (movieDataCache && ['want-to-watch', 'watched'].includes(watchlist.id)) {
            movieCount = this.getMovieCount(watchlist.id, movieDataCache, [], currentUser);
        }
        else {
            movieCount = ((_a = watchlist.movieDetails) === null || _a === void 0 ? void 0 : _a.length) || 0;
        }
        return {
            id: watchlist.id,
            name: watchlist.name,
            description: watchlist.description,
            movieCount,
            isPrivate: watchlist.isPrivate || false,
            creator,
            isOwned,
            isFollowed,
            followerCount: watchlist.followerCount || 0,
            collaborators: watchlist.collaborators,
            collaboratorProfiles: watchlist.collaboratorProfiles
        };
    }
    /**
     * Generate user initials from display name
     */
    static getUserInitials(displayName) {
        if (!displayName)
            return 'U';
        const names = displayName.trim().split(' ');
        if (names.length === 1) {
            return names[0].charAt(0).toUpperCase();
        }
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    /**
     * Generate background color for user initials
     */
    static getInitialsBackgroundColor(displayName) {
        if (!displayName)
            return '#6B73FF';
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
            '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
            '#5856D6', '#FF3B30', '#FF9500', '#FFCC02',
            '#34C759', '#00C7BE', '#007AFF', '#5AC8FA',
            '#AF52DE', '#FF2D92', '#A2845E'
        ];
        let hash = 0;
        for (let i = 0; i < displayName.length; i++) {
            hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }
    /**
     * Filter watchlists based on privacy and ownership
     */
    static filterWatchlistsForDisplay(watchlists, currentUser, showPrivate = true) {
        return watchlists.filter(watchlist => {
            if (watchlist.createdBy === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid)) {
                return true;
            }
            if (!showPrivate && watchlist.isPrivate) {
                return false;
            }
            return true;
        });
    }
    /**
     * Sort watchlists by various criteria
     */
    static sortWatchlists(watchlists, sortBy = 'name', ascending = true) {
        const sorted = [...watchlists].sort((a, b) => {
            var _a, _b;
            let comparison = 0;
            switch (sortBy) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'created':
                    comparison = (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0);
                    break;
                case 'modified':
                    comparison = (Number(a.lastModified) || 0) - (Number(b.lastModified) || 0);
                    break;
                case 'movieCount':
                    const aCount = ((_a = a.movieDetails) === null || _a === void 0 ? void 0 : _a.length) || 0;
                    const bCount = ((_b = b.movieDetails) === null || _b === void 0 ? void 0 : _b.length) || 0;
                    comparison = aCount - bCount;
                    break;
                default:
                    comparison = a.name.localeCompare(b.name);
            }
            return ascending ? comparison : -comparison;
        });
        return sorted;
    }
    /**
     * Format watchlist description for display
     */
    static formatDescription(description, maxLength = 100) {
        if (!description)
            return '';
        if (description.length <= maxLength) {
            return description;
        }
        return description.substring(0, maxLength - 3) + '...';
    }
    /**
     * Check if user is a collaborator on a watchlist
     */
    static isUserCollaborator(watchlist, userId) {
        if (!userId || !watchlist.collaborators)
            return false;
        return watchlist.collaborators.includes(userId);
    }
    /**
     * Get watchlist privacy label
     */
    static getPrivacyLabel(isPrivate) {
        return isPrivate ? 'Private' : 'Public';
    }
    /**
     * Get watchlist type label for default watchlists
     */
    static getWatchlistTypeLabel(type) {
        const labels = {
            'want-to-watch': 'Want to Watch',
            'watched': 'Watched',
            'main-watchlist': 'Watchlist',
            'classics': 'Classics',
            'villeneuve': 'Denis Villeneuve',
            'ghibli': 'Studio Ghibli',
            'comedy': 'Comedy'
        };
        return labels[type] || type;
    }
}
exports.WatchlistDisplayService = WatchlistDisplayService;
